/**
 * Meta Pixel: one `PageView` for an arrival on the landing page, unless the
 * voter has turned advertising partners off (Your Privacy Choices).
 *
 * Moved out of `index.html` so it can honor that opt-out. The arrival rules are
 * unchanged: the production host only, the landing path, outside the partner
 * embed, not a `?address=` research link, and never under Global Privacy
 * Control. `autoConfig` and push-state tracking stay off, so the snippet sends
 * nothing beyond the one `PageView`.
 */
import { isEmbed } from '@/config/embed';
import { getMetaPixelHost } from '@/config/env';
import { getCapturedResearchAddress } from './researchAddressBootstrap';
import { isGpcEnabled, whenAdConsentGranted } from './adConsentStore';
import { isReportableUrl } from './adPartnerUrl';

const PIXEL_ID = '24035620172783555';
const SCRIPT_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: Fbq;
  loaded: boolean;
  version: string;
  disablePushState?: boolean;
};

export const isEligibleMetaArrival = ({
  hostname,
  allowedHost,
  pathname,
  embed,
  hasResearchAddress,
  gpc,
}: {
  hostname: string;
  allowedHost: string | undefined;
  pathname: string;
  embed: boolean;
  hasResearchAddress: boolean;
  gpc: boolean;
}): boolean =>
  !!allowedHost &&
  hostname.toLowerCase() === allowedHost &&
  pathname === '/' &&
  !embed &&
  !hasResearchAddress &&
  !gpc;

// The vendor's base code, unchanged in behavior: queue calls on a stub `fbq`
// until `fbevents.js` loads and drains it.
const loadAndTrack = (): void => {
  const w = window as unknown as { fbq?: Fbq; _fbq?: Fbq };
  if (w.fbq) return;
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args);
    else fbq.queue.push(args);
  } as Fbq;
  w.fbq = fbq;
  if (!w._fbq) w._fbq = fbq;
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  const script = document.createElement('script');
  script.async = true;
  script.src = SCRIPT_SRC;
  document.head.appendChild(script);

  fbq.disablePushState = true;
  fbq('set', 'autoConfig', false, PIXEL_ID);
  fbq('init', PIXEL_ID);
  fbq('track', 'PageView');
};

export const loadMetaPixel = (): void => {
  try {
    const eligible = isEligibleMetaArrival({
      hostname: window.location.hostname,
      allowedHost: getMetaPixelHost(),
      pathname: window.location.pathname,
      embed: isEmbed(),
      hasResearchAddress: getCapturedResearchAddress() !== null,
      gpc: isGpcEnabled(),
    });
    if (!eligible) return;
    // The pixel reports the page it runs on, so turning partners back on later sends the
    // PageView only from a URL without identifiers (see `isReportableUrl`).
    const landingHref = window.location.href;
    whenAdConsentGranted(
      () => {
        try {
          loadAndTrack();
        } catch {
          // Tracking never interrupts the app.
        }
      },
      () => isReportableUrl(window.location.href, landingHref)
    );
  } catch {
    // Tracking never interrupts the app.
  }
};
